import type { PlayerRef } from "@remotion/player";
import { TOTAL_FRAMES } from "@remotion-src/lib/theme";
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from "react";

/**
 * スクロール連動 Remotion Player
 *
 * ヒーローセクション下に配置し、スクロール量に応じて
 * Remotion コンポジションのフレームを seekTo() で制御する。
 *
 * - sticky 配置で Player をビューポート中央に固定
 * - スクロール範囲のコンテナ内でのみフレームが進行
 * - コンテナを抜けるとPlayerも一緒にスクロールアウト
 * - SSG 対応: クライアントのみでレンダリング
 */

/** スクロール範囲 = 各シーンに十分なスクロール余白を確保 (px換算で ~10画面分) */
const SCROLL_HEIGHT_VH = 1000;

/** Player 本体を lazy import（SSG 時にサーバーサイドで読み込まないため） */
const LazyPlayerInner = lazy(() => import("./ScrollDemoPlayerInner"));

export function ScrollDemoPlayer() {
  const playerRef = useRef<PlayerRef>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  // SSG対応: クライアントのみでマウント
  useEffect(() => {
    setIsMounted(true);
  }, []);

  // IntersectionObserver でコンテナの可視性を監視
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0 },
    );
    observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // スクロール位置 → フレーム番号のマッピング
  const handleScroll = useCallback(() => {
    const container = containerRef.current;
    const player = playerRef.current;
    if (!container || !player) return;

    const rect = container.getBoundingClientRect();
    const containerHeight = container.offsetHeight;
    const viewportHeight = globalThis.innerHeight;

    // コンテナ上端がビューポート下端に来た時 = 0%
    // コンテナ下端がビューポート上端に来た時 = 100%
    const scrollProgress = Math.max(0, Math.min(1, -rect.top / (containerHeight - viewportHeight)));

    const frame = Math.round(scrollProgress * (TOTAL_FRAMES - 1));
    player.seekTo(frame);
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    globalThis.addEventListener("scroll", handleScroll, { passive: true });
    // 初回計算
    handleScroll();
    return () => globalThis.removeEventListener("scroll", handleScroll);
  }, [isVisible, handleScroll]);

  return (
    <div
      ref={containerRef}
      className="relative w-full bg-gradient-to-b from-transparent via-white to-white pt-12"
      style={{ height: `${SCROLL_HEIGHT_VH}vh` }}
    >
      <div className="pointer-events-none absolute inset-x-0 top-0 h-[460px] bg-[radial-gradient(circle_at_50%_0%,rgba(49,85,214,0.14),transparent_66%)]" />
      <div className="pointer-events-none absolute inset-x-0 top-10 mx-auto h-px max-w-5xl bg-gradient-to-r from-transparent via-brand-200 to-transparent" />
      <div className="sticky top-16 flex h-[calc(100vh-64px)] w-full items-center justify-center">
        <div className="mx-auto w-full max-w-6xl px-6">
          <div className="mb-6 text-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-brand-100 bg-white/85 px-4 py-2 text-[12px] font-medium text-brand-600 shadow-sm backdrop-blur">
              <span className="mi text-[16px]">play_circle</span>
              DEMO
            </span>
            <h2 className="mt-4 text-[28px] font-black leading-tight tracking-tight text-ink-900 md:text-[40px]">
              Chrome拡張機能でスカウト管理
            </h2>
            <p className="mx-auto mt-3 max-w-[680px] text-[14px] leading-relaxed text-ink-600 md:text-[15px]">
              ↓ 下にスクロールしてください ↓
            </p>
          </div>
          <div className="overflow-hidden rounded-[28px] border border-white/80 bg-white p-2 shadow-[0_32px_120px_rgba(15,23,42,0.18)]">
            <div className="flex h-11 items-center justify-between rounded-t-[22px] border border-b-0 border-ink-100 bg-ink-50/80 px-4">
              <div className="flex items-center gap-2">
                <span className="h-2.5 w-2.5 rounded-full bg-rose-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-amber-400" />
                <span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              </div>
              <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-ink-400">
                Scout Mail AI Demo
              </div>
              <div className="w-[54px]" />
            </div>
            <div className="overflow-hidden rounded-b-[22px] border border-ink-100">
              {isMounted ? (
                <Suspense
                  fallback={
                    <div
                      className="flex items-center justify-center bg-slate-100"
                      style={{ aspectRatio: "16/9" }}
                    >
                      <div className="text-on-surface-variant/40">読み込み中...</div>
                    </div>
                  }
                >
                  <LazyPlayerInner ref={playerRef} />
                </Suspense>
              ) : (
                <div
                  className="flex items-center justify-center bg-slate-100"
                  style={{ aspectRatio: "16/9" }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
